import React from 'react'
import {getRecurrenceLists, getWeekDay} from '../../utils/recurrence'
import {isSameDate} from '../../utils/time'
import styled from 'styled-components'
import ListSelect from '../../components/ListSelect/ListSelect'
import CalendarField from '../../components/Calendar/CalendarField/CalendarField'
import WeekDaySelect, {
  DIRECTION
} from '../../components/WeekDaySelect/WeekDaySelect'
import {
  useRecurrenceState,
  useRecurrenceDispatch,
  setRecurrenceValue
} from '../../context/RecurrenceContext'
import {_L, useLocaleState} from '../../context/LocaleContext'

const RecurrencePattern = () => {
  const recurrence = useRecurrenceState()
  const recurrenceDispatch = useRecurrenceDispatch()
  const localeState = useLocaleState()

  const {
    date_start,
    date_end,
    recurrenceType,
    weekDays,
    frequencyDay,
    frequency
  } = recurrence

  const {TYPES, DAYS, FREQUENCY} = getRecurrenceLists({localeState})

  const onSelectRecurrenceType = type => {
    let newState = {recurrenceType: type.value}
    // Set the selected week day to the current date
    if (type.value === 'week') {
      let dayIndex = getWeekDay(new Date())
      let weekDays = [0, 0, 0, 0, 0, 0, 0]
      weekDays[dayIndex] = 1
      newState = {...newState, ...{weekDays}}
    } else if (type.value === 'month') {
      if (isSameDate(date_start, date_end)) {
        date_end.setMonth(date_end.getMonth() + 1)
        newState = {...newState, ...{date_end}}
      }
    }

    setRecurrenceValue({recurrenceDispatch, payload: newState})
  }

  const onSelectStartDate = date => {
    let date_start = new Date(date)
    let newState = {date_start}

    if (recurrenceType === 'none') {
      newState = {date_start, date_end: date_start}
    } else {
      if (date_end.getTime() < date_start.getTime()) {
        newState.date_end = date_start
      }
    }

    setRecurrenceValue({recurrenceDispatch, payload: newState})
  }

  const onSelectEndDate = date =>
    setRecurrenceValue({
      recurrenceDispatch,
      payload: {date_end: new Date(date)}
    })

  const onSelectFrequency = frequency =>
    setRecurrenceValue({
      recurrenceDispatch,
      payload: {frequency: frequency.value}
    })

  const onSelectDays = day =>
    setRecurrenceValue({recurrenceDispatch, payload: {frequencyDay: day.value}})

  const onWeekDaySelect = weekDays =>
    setRecurrenceValue({
      recurrenceDispatch,
      payload: {weekDays: weekDays.slice(0)}
    })

  const showEndDate = recurrenceType !== 'none'
  const showDaysOfWeek = recurrenceType === 'week'
  const showFrequency = recurrenceType === 'month'

  return (
    <Container>
      <Title>{_L({key: 'Input Meeting Info', localeState})}</Title>
      <RecurrenceContainer>
        <Row>
          <ComponentContainer style={{maxWidth: 260}}>
            <Label>{_L({key: 'Recurrence Pattern', localeState})}</Label>
            <ListSelect
              options={TYPES}
              placeholder=""
              isSearchable={false}
              value={TYPES.filter(({value}) => value === recurrenceType)}
              onChange={onSelectRecurrenceType}
            />
          </ComponentContainer>
          <DatesContainer>
            <SmallComponentContainer>
              <Label>{_L({key: 'Start Date', localeState})}</Label>
              <CalendarField
                date={date_start.toISOString()}
                onSelect={onSelectStartDate}
              />
            </SmallComponentContainer>

            {showEndDate && (
              <SmallComponentContainer>
                <Label>{_L({key: 'End Date', localeState})}</Label>
                <CalendarField
                  date={date_end.toISOString()}
                  onSelect={onSelectEndDate}
                />
              </SmallComponentContainer>
            )}
          </DatesContainer>
        </Row>

        {showDaysOfWeek && (
          <Row>
            <ComponentContainer style={{paddingRight: 10}}>
              <Label>{_L({key: 'Days of Week', localeState})}</Label>
              <WeekDaySelect
                value={weekDays}
                onChange={onWeekDaySelect}
                direction={DIRECTION.HORIZONTAL}
              />
            </ComponentContainer>
          </Row>
        )}

        {showFrequency && (
          <Row>
            <Label style={{paddingLeft: 20}}>
              {_L({key: 'Every', localeState})}
            </Label>
            <FrequencyContainer>
              <ComponentContainer style={{maxWidth: 260, marginBottom: 16}}>
                <ListSelect
                  options={FREQUENCY}
                  placeholder=""
                  isSearchable={false}
                  value={FREQUENCY.filter(({value}) => value === frequency)}
                  onChange={onSelectFrequency}
                />
              </ComponentContainer>

              <ComponentContainer style={{maxWidth: 260}}>
                <ListSelect
                  options={DAYS}
                  placeholder=""
                  isSearchable={false}
                  value={DAYS.filter(({value}) => value === frequencyDay)}
                  onChange={onSelectDays}
                />
              </ComponentContainer>
            </FrequencyContainer>
          </Row>
        )}
      </RecurrenceContainer>
    </Container>
  )
}

export default RecurrencePattern

const Container = styled.div`
  padding: 20px 30px 0 30px;
  background-color: #eff0f3;

  @media (max-width: 420px) {
    padding: 20px 0 0 0;
  }
`

const Title = styled.div`
  font-size: 16px;
  color: #a4a9b7;
  font-weight: 600;

  @media (max-width: 420px) {
    padding-left: 20px;
  }
`

const RecurrenceContainer = styled.div`
  width: 100%;
  margin-top: 10px;
  border-radius: 6px;
  background-color: #ffffff;
  box-sizing: border-box;
  padding-bottom: 20px;

  @media (max-width: 1025px) {
    padding-left: 0;
  }

  @media (max-width: 420px) {
    border-radius: 0;
  }
`

const ComponentContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding-left: 20px;
`

const SmallComponentContainer = styled.div`
  box-sizing: border-box;
  width: 180px;
  padding-left: 20px;
`

const Label = styled.div`
  font-size: 16px;
  color: #a4a9b7;
  box-sizing: border-box;
  padding-top: 18px;
  padding-bottom: 10px;
`

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex-wrap: wrap;
`

const DatesContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex-wrap: wrap;
  padding-right: 10px;
`

const FrequencyContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex-wrap: wrap;
`
